// セッション開始フォーム(設計書 §3.4.4 POST /v1/sessions)
// プロファイル選択 + 解像度/フレームレート/ビットレート + 音声処理オプション。
// 実際のカメラプレビュー & 取得は VDO Ninja iframe(LiveSessionView 内)が
// 担うため、本フォームでは getUserMedia を呼ばない。
// (Phase 5 Step 2: iframe 経由で配信、ここでは API へ渡す encoding 設定のみ収集)

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import { useCreateSession, type CreateSessionInput } from '../../api/sessions';
import {
  PLATFORM_LABELS,
  useStreamingProfiles,
  type StreamingProfile,
} from '../../api/streaming-profiles';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import type { CaptureSettings } from '../../lib/media-capture';

const RESOLUTION_PRESETS: Array<{ key: string; label: string; w: number; h: number }> = [
  { key: '720p', label: '1280×720(720p)', w: 1280, h: 720 },
  { key: '1080p', label: '1920×1080(1080p)', w: 1920, h: 1080 },
  { key: '480p', label: '854×480(480p)', w: 854, h: 480 },
];

const FRAMERATES = [30, 60, 24];
const BITRATE_PRESETS = [
  { kbps: 2500, label: '2,500 kbps(標準)' },
  { kbps: 4500, label: '4,500 kbps(高画質)' },
  { kbps: 1500, label: '1,500 kbps(低帯域)' },
  { kbps: 6000, label: '6,000 kbps(最高画質)' },
];

function settingsToResolutionKey(s: CaptureSettings): string {
  const match = RESOLUTION_PRESETS.find((p) => p.w === s.width && p.h === s.height);
  return match?.key ?? '720p';
}

export interface VdoSessionParams {
  room_id: string;
  push_id: string;
  signaling_url: string;
  /** 設計書 §5.3.2: WebSocket upgrade で検証される push 用 HS256 JWT */
  push_jwt: string;
  /** 同上、view 側用。本番では agent 経由で配布されるが、dev では
   *  別タブのブラウザで視聴 URL を開けるように API レスポンスにも含めている。 */
  view_jwt: string;
}

interface StartSessionFormProps {
  settings: CaptureSettings;
  onSettingsChange: (patch: Partial<CaptureSettings>) => void;
  onSessionCreated: (params: VdoSessionParams) => void;
  /** 親 StreamingPage が useCurrentSession の error を整形して渡す。
   *  設定済みの場合はフォーム冒頭にバナーで表示する(再試行を妨げないために
   *  フォーム自体は触らず、上に通知だけ出す)。 */
  sessionFetchError?: string | null;
}

export function StartSessionForm({
  settings,
  onSettingsChange,
  onSessionCreated,
  sessionFetchError,
}: StartSessionFormProps) {
  const profilesQuery = useStreamingProfiles();
  const createMutation = useCreateSession();

  const [profileUlid, setProfileUlid] = useState<string>('');

  const profiles = profilesQuery.data ?? [];
  const usable = profiles.filter((p) => p.available_for_plan);

  // プロファイル一覧が揃った時点でデフォルトを優先選択(以降は手動切替を尊重)
  useEffect(() => {
    if (profileUlid !== '') return;
    if (usable.length === 0) return;
    const initial = usable.find((p) => p.is_default)?.ulid ?? usable[0]?.ulid ?? '';
    if (initial) setProfileUlid(initial);
  }, [profileUlid, usable]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!profileUlid) return;
    const input: CreateSessionInput = {
      streaming_profile_ulid: profileUlid,
      browser_settings: {
        video: {
          ...(settings.videoDeviceId ? { device_id: settings.videoDeviceId } : {}),
          width: settings.width,
          height: settings.height,
          framerate: settings.framerate,
          max_bitrate_kbps: settings.bitrateKbps,
        },
        audio: {
          ...(settings.audioDeviceId ? { device_id: settings.audioDeviceId } : {}),
          echo_cancellation: settings.echoCancellation,
          noise_suppression: settings.noiseSuppression,
          auto_gain_control: settings.autoGainControl,
        },
      },
    };
    try {
      const result = await createMutation.mutateAsync(input);
      // VDO Ninja iframe を組み立てるための情報を親 StreamingPage に渡す。
      onSessionCreated({
        room_id: result.webrtc.room_id,
        push_id: result.webrtc.push_id,
        signaling_url: result.webrtc.signaling_url,
        push_jwt: result.webrtc.push_jwt,
        view_jwt: result.webrtc.view_jwt,
      });
      // useCurrentSession が invalidate されるので親 StreamingPage が LiveSessionView に切替
    } catch {
      // エラーは下のメッセージで表示
    }
  }

  function applyResolution(key: string) {
    const preset = RESOLUTION_PRESETS.find((p) => p.key === key);
    if (preset) onSettingsChange({ width: preset.w, height: preset.h });
  }

  return (
    <AppLayout
      title="配信を開始"
      description="配信先と出力設定を選んで開始してください。配信開始後にカメラ/マイクの許可を求めます。"
    >
      {sessionFetchError && (
        <Card>
          <p
            role="alert"
            style={{
              margin: 0,
              fontSize: '12px',
              color: 'var(--color-error-text)',
            }}
          >
            {sessionFetchError}
          </p>
        </Card>
      )}

      {profilesQuery.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            プロファイルを読み込み中…
          </p>
        </Card>
      )}

      {profilesQuery.data && profiles.length === 0 && (
        <Card title="配信先が登録されていません">
          <p style={{ margin: '0 0 14px', color: 'var(--color-text-secondary)' }}>
            配信を開始するには、まず配信先プロファイル(FC2 / YouTube 等)を登録してください。
          </p>
          <div style={{ maxWidth: 220 }}>
            <Link to="/streaming-profiles">
              <Button variant="secondary">配信先を登録する</Button>
            </Link>
          </div>
        </Card>
      )}

      {profiles.length > 0 && (
        <form onSubmit={handleSubmit}>
          <Card title="配信先" description="登録済みの配信先プロファイルから選択します。">
            <Select
              label="プロファイル"
              value={profileUlid}
              onChange={(e) => setProfileUlid(e.target.value)}
            >
              {profiles.map((p) => (
                <option key={p.ulid} value={p.ulid} disabled={!p.available_for_plan}>
                  {profileOptionLabel(p)}
                </option>
              ))}
            </Select>
          </Card>

          <Card title="映像設定">
            <Toggle
              label="画面共有モードで配信する(カメラの代わりに画面を送出)"
              checked={settings.useScreenshare}
              onChange={(v) => onSettingsChange({ useScreenshare: v })}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Select
                label="解像度"
                value={settingsToResolutionKey(settings)}
                onChange={(e) => applyResolution(e.target.value)}
              >
                {RESOLUTION_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </Select>
              <Select
                label="フレームレート"
                value={String(settings.framerate)}
                onChange={(e) => onSettingsChange({ framerate: Number(e.target.value) })}
              >
                {FRAMERATES.map((f) => (
                  <option key={f} value={f}>
                    {f} fps
                  </option>
                ))}
              </Select>
            </div>
            <Select
              label="ビットレート上限"
              value={String(settings.bitrateKbps)}
              onChange={(e) => onSettingsChange({ bitrateKbps: Number(e.target.value) })}
            >
              {BITRATE_PRESETS.map((b) => (
                <option key={b.kbps} value={b.kbps}>
                  {b.label}
                </option>
              ))}
            </Select>
          </Card>

          <Card title="音声処理">
            <Toggle
              label="エコーキャンセレーション"
              checked={settings.echoCancellation}
              onChange={(v) => onSettingsChange({ echoCancellation: v })}
            />
            <Toggle
              label="ノイズ抑制"
              checked={settings.noiseSuppression}
              onChange={(v) => onSettingsChange({ noiseSuppression: v })}
            />
            <Toggle
              label="自動ゲイン調整"
              checked={settings.autoGainControl}
              onChange={(v) => onSettingsChange({ autoGainControl: v })}
            />
          </Card>

          {createMutation.error && (
            <Card>
              <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
                {createMutation.error instanceof ApiClientError
                  ? createMutation.error.message
                  : '配信の開始に失敗しました'}
              </p>
            </Card>
          )}

          <div style={{ maxWidth: 240, marginTop: 8 }}>
            <Button
              type="submit"
              disabled={!profileUlid || createMutation.isPending || usable.length === 0}
            >
              {createMutation.isPending ? '開始中…' : '配信を開始'}
            </Button>
          </div>
        </form>
      )}
    </AppLayout>
  );
}

function profileOptionLabel(p: StreamingProfile): string {
  const platform = PLATFORM_LABELS[p.platform];
  const tag = p.is_default ? '(デフォルト)' : '';
  const restricted = p.available_for_plan ? '' : ' [Pro 限定]';
  return `${p.name} — ${platform}${tag}${restricted}`;
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '0 0 8px',
        fontSize: '12px',
        color: 'var(--color-text-secondary)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
